import { defineStore } from 'pinia'
import { computed } from 'vue'
import otsImage from '../assets/ots512.png'
import project2Image from '../assets/dunya_evim_logo512.png'
import stubyImage from '../assets/stuby.png'
import ilacSaatiImage from '../assets/ilac_saati_logo.png'
import elestirImage from '../assets/elestir_logo.png'
import battleshipImage from '../assets/battleship.png'
import csvFormatterImage from '../assets/csv_formatter_logo.png'
import { useLanguageStore } from '@/stores/language'

export interface LocalizedString {
  tr: string
  en: string
}

export interface ProjectData {
  id: string
  title: LocalizedString
  description: LocalizedString
  fullDescription?: LocalizedString
  image: string
  appStoreLink: string
  playStoreLink: string
  technologies: string[]
  projectLink: string
  development: boolean
  websiteLink: string
}

export interface Project {
  id: string
  title: string
  description: string
  fullDescription?: string
  image: string
  appStoreLink: string
  playStoreLink: string
  technologies: string[]
  projectLink: string
  development: boolean
  websiteLink: string
}

const projectsData: ProjectData[] = [
  {
    id: 'ots',
    title: {
      tr: 'OTS',
      en: 'OTS',
    },
    description: {
      tr: 'OTS, Instagram ve sosyal medyada butik satışı yapanlar için geliştirilmiş profesyonel bir sipariş takip programı ve müşteri yönetim sistemidir. Sipariş takibi, kurye konumu, stok yönetimi, damacana/depozito takibi ve web paneli ile işinizi kolaylaştırır.',
      en: 'OTS is a professional order tracking software and customer management system developed for boutique sellers on Instagram and social media. It simplifies your business with order tracking, courier location, stock management, deposit tracking and a web panel.',
    },
    fullDescription: {
      tr: `OTS, siparişlerinizi, müşterilerinizi ve ürünlerinizi tek yerden yönetmenizi sağlayan mobil uygulama ve web paneli ile çalışan bir sipariş takip sistemidir. Aynı hesap ile telefon, tablet veya bilgisayardan erişebilirsiniz; verileriniz tüm cihazlarda anında senkronize olur.

Hedef kitle
Instagram, Facebook ve WhatsApp gibi sosyal medyada satış yapan bireysel satıcılar, küçük butikler, pop-up shop'lar, kişisel markalar ve düşük hacimli e-ticaret girişimleri için idealdir.

Özellikler

• Sipariş yönetimi: Sipariş oluşturma, düzenleme; durum takibi (Beklemede, Hazırlanıyor, Yolda, Tamamlandı); nakliye bilgisi; müşteriye WhatsApp, SMS veya e-posta ile paylaşım; takip linki ile sipariş durumu paylaşma.

• Müşteri yönetimi: Müşteri ekleme ve düzenleme, iletişim ve adres bilgileri, bakiye takibi, müşteri notları, sipariş geçmişi.

• Ürün ve kategori: Ürün kataloğu, birim fiyat, stok, vergi ve indirim; kategorilere göre organize etme ve filtreleme; hızlı sipariş oluşturma.

• Fiyat hesaplama: Otomatik ürün toplamı, ara toplam, KDV, indirim, kargo ve ek ücretler; genel toplamın anlık hesaplanması.

• Mobil ve web paneli: iOS/Android uygulaması ve tarayıcıdan erişilen web paneli; aynı Firebase hesabı ve veri; ofiste web, sahada mobil ile aynı verilerle çalışma.

• Dil, para birimi ve tema: Çoklu dil ve para birimi (₺, $, €); açık ve koyu tema.

• Dashboard ve raporlama: Sipariş sayıları, özet bilgiler, geniş ekranda tablolar ve filtreler.

• Destek: WhatsApp destek, kullanım koşulları, gizlilik politikası ve üyelik sözleşmesi erişimi.`,
      en: `OTS is an order tracking system with a mobile app and web panel that lets you manage your orders, customers and products in one place. You can access it from your phone, tablet or computer with the same account — your data syncs instantly across all devices.

Target Audience
Ideal for individual sellers, small boutiques, pop-up shops, personal brands and low-volume e-commerce ventures selling on social media platforms like Instagram, Facebook and WhatsApp.

Features

• Order management: Create and edit orders; status tracking (Pending, Preparing, On the Way, Completed); shipping info; share with customers via WhatsApp, SMS or email; share order status via a tracking link.

• Customer management: Add and edit customers, contact and address details, balance tracking, customer notes, order history.

• Products & categories: Product catalog, unit price, stock, tax and discount; organize and filter by category; quick order creation.

• Price calculation: Automatic product totals, subtotal, VAT, discount, shipping and additional charges; live grand total calculation.

• Mobile & web panel: iOS/Android app and browser-based web panel; same Firebase account and data; work with the same data on web at the office and mobile in the field.

• Language, currency & theme: Multiple languages and currencies (₺, $, €); light and dark theme.

• Dashboard & reporting: Order counts, summary info, tables and filters on wide screens.

• Support: WhatsApp support, access to terms of use, privacy policy and membership agreement.`,
    },
    image: otsImage,
    appStoreLink: 'https://apps.apple.com/us/app/order-tracking-system/id6474212911',
    playStoreLink:
      'https://play.google.com/store/apps/details?id=com.efesbit.siparis_takip_sistem',
    technologies: ['Flutter', 'Firebase'],
    projectLink: '/projects/ots',
    development: false,
    websiteLink: 'https://otsapp.website',
  },
  {
    id: 'elestir',
    title: {
      tr: 'Eleştir',
      en: 'Eleştir',
    },
    description: {
      tr: 'Eleştir.net, gündemdeki konuları, başlıkları ve gelişmeleri özgürce eleştirip yorumlayabileceğiniz katılımcı bir sözlük ve tartışma platformudur.',
      en: 'Eleştir.net is a participatory dictionary and discussion platform where you can freely criticize and comment on current topics, headlines, and developments.',
    },
    image: elestirImage,
    appStoreLink: 'https://apps.apple.com/us/app/elestir-net/id6794832271',
    playStoreLink: 'https://play.google.com/store/apps/details?id=com.efestech.elestir_net',
    technologies: ['iOS', 'Android', 'Vue.js', 'Firebase'],
    projectLink: '/projects/elestir',
    development: false,
    websiteLink: 'https://elestir.net',
  },
  {
    id: 'stuby',
    title: {
      tr: 'Stuby',
      en: 'Stuby',
    },
    description: {
      tr: 'Stuby, okul öncesi ve ilkokul çağındaki çocuklar için özel tasarlanmış eğlenceli ve eğitici çocuk oyunları koleksiyonudur. Çocukların internetsiz ve güvenli şekilde eğlenerek öğrenmesini sağlayan eğitici mini oyunlar barındırır.',
      en: 'Stuby is a collection of fun and educational kids games specially designed for preschool and primary school children. It features educational mini games that allow children to learn while having fun offline and safely.',
    },
    image: stubyImage,
    appStoreLink: 'https://apps.apple.com/us/app/stuby-mini-oyunlar/id6756493936',
    playStoreLink: 'https://play.google.com/store/apps/details?id=com.efestech.stuby',
    technologies: ['Flutter', 'AI Based'],
    projectLink: '/projects/stuby',
    development: false,
    websiteLink: '#',
  },
  {
    id: 'ilac-saati',
    title: {
      tr: 'İlaç Saati',
      en: 'Ilac Saati',
    },
    description: {
      tr: 'İlaç Saati, siz ve sevdikleriniz için geliştirilmiş akıllı bir ilaç hatırlatıcı uygulaması ve ilaç takip takvimidir. Birden fazla kişi için ilaç saatlerini kurun, günlük dozları ve biten ilaçları takip ederek zamanında bildirim alın.',
      en: 'Ilac Saati is a smart medication reminder app and medication tracking calendar developed for you and your loved ones. Set pill reminders for multiple people, track daily dosages and refilling times to receive timely notifications.',
    },
    fullDescription: {
      tr: `İlaç Saati, ilaç yönetimini kolaylaştıran kapsamlı bir mobil uygulamadır.

Temel Özellikler:

• Çoklu Kişi Desteği: Aile üyelerinin tüm ilaçlarını tek uygulamadan yönetin
• Akıllı Hatırlatıcılar: Zamanında bildirimlerle ilaç saatlerini kaçırmayın
• Günlük Takip: İlaç alım durumlarını kaydedin ve takip edin
• Büyük Arayüz: Yaşlı kullanıcılar için özel tasarlanmış, okunması kolay ekranlar
• Basit Kullanım: Karmaşık menüler olmadan, sezgisel tasarım

Hedef Kullanıcılar:

• Düzenli ilaç kullanan hastalar
• Yaşlı bireyler ve aileleri
• Kronik hastalığı olan kişiler
• Bakım veren aile üyeleri
• Çoklu ilaç kullanan herkes

Avantajlar:

• Yerel veri depolama (gizlilik odaklı)
• İnternet bağlantısı gerektirmez
• Reklam içermez (premium sürüm)
• Çoklu dil desteği (Türkçe/İngilizce)
• Tüm yaş grupları için uygun tasarım`,
      en: `Ilac Saati is a comprehensive mobile app that simplifies medication management.

Key Features:

• Multiple Profiles: Manage medications for all family members from a single app
• Smart Reminders: Never miss a dose with timely notifications
• Daily Tracking: Record and track medication intake statuses
• Large Interface: Easy-to-read screens specially designed for elderly users
• Simple to Use: Intuitive design with no complex menus

Target Users:

• Patients who take medication regularly
• Elderly individuals and their families
• People with chronic conditions
• Caregiving family members
• Anyone managing multiple medications

Advantages:

• Local data storage (privacy-focused)
• No internet connection required
• Ad-free (premium version)
• Multi-language support (Turkish / English)
• Suitable design for all age groups`,
    },
    image: ilacSaatiImage,
    appStoreLink: 'https://apps.apple.com/us/app/i-laç-saati/id6755570827',
    playStoreLink: 'https://play.google.com/store/apps/details?id=com.efestech.ilacsaat',
    technologies: ['Flutter', 'Local Storage'],
    projectLink: '/projects/ilac-saati',
    development: false,
    websiteLink: '#',
  },
  {
    id: 'dunya-evim',
    title: {
      tr: 'Dünya Evim',
      en: 'Dünya Evim',
    },
    description: {
      tr: 'Türkiye dışından ev satın almak veya kiralamak mı istiyorsunuz? Dünya Evim ile dünyanın dört bir yanındaki evleri keşfedin! Uygulamamızda, dünya genelindeki emlakçılar ve ev sahipleri tarafından listelenen evleri kolayca inceleyebilir, beğendiğiniz evlerle ilgili ilan sahiplerine doğrudan ulaşabilirsiniz. Unutmayın, Dünya Evim sadece bir platformdur ve ilanların doğruluğundan sorumlu değildir. İlan detayları ve evin güncel durumu hakkında bilgi almak için lütfen ilan sahibiyle iletişime geçin. Dünya Evim ile hayallerinizdeki evi bulmak artık çok kolay!',
      en: 'Thinking of buying or renting a home outside Turkey? Discover homes from every corner of the world with Dünya Evim! Browse listings posted by real estate agents and homeowners worldwide, and reach out directly to sellers for the homes you love. Please note that Dünya Evim is a platform only and is not responsible for the accuracy of listings. Contact the listing owner for details and the current status of any property. Finding your dream home is now easier than ever with Dünya Evim!',
    },
    image: project2Image,
    appStoreLink: 'https://apps.apple.com/tr/app/d%C3%BCnya-evim/id6477548101',
    playStoreLink: 'https://play.google.com/store/apps/details?id=com.dunyaevim.dunya_evim',
    technologies: ['Flutter', 'Firebase'],
    projectLink: '/projects/dunya-evim',
    development: false,
    websiteLink: '#',
  },
  {
    id: 'battleship',
    title: {
      tr: 'Battleship.pro',
      en: 'Battleship.pro',
    },
    description: {
      tr: 'Battleship.pro, modern WebGL ve Three.js teknolojileriyle geliştirilmiş, gerçek zamanlı çok oyunculu 3D amiral battı (deniz savaşı) oyunudur. Tarayıcı üzerinden arkadaşlarınızla veya yapay zekaya karşı amansız deniz savaşlarına katılın.',
      en: 'Battleship.pro is a modern real-time multiplayer 3D naval warfare game built with WebGL and Three.js. Engage in tactical naval battles against friends via web browsers or challenge smart AI admirals.',
    },
    fullDescription: {
      tr: `Battleship.pro (3D Amiral Battı), klasik amiral battı oyununun modern WebGL 3D grafikleri ve gerçek zamanlı WebSocket (Socket.io) altyapısı ile yeniden yorumlanmış halidir.

Temel Özellikler:

• 3D Gerçek Zamanlı Deniz Savaşı: Kamera açılarını özelleştirebileceğiniz, gerçekçi deniz ve patlama efektlerine sahip 3D savaş alanı
• Çevrimiçi Çok Oyunculu (Multiplayer): Anlık oda oluşturma, oda kodu paylaşımı, davet linki ve hızlı otomatik eşleşme
• Yapay Zeka Solo Savaş: 3 farklı zorluk seviyesinde (Acemi, Taktikçi, Amiral) gelişmiş yapay zeka amirallere karşı mücadele
• Liderlik Tablosu & İstatistikler: Kazanç/kayıp oranları, isabet yüzdeleri ve canlı sıralamalar
• İnteraktif Filo Intel & Takip: Uçak gemisi, amiral gemisi, kruvazör, denizaltı ve muhrip filolarının canlı sağlık takibi
• Çoklu Dil Desteği: Türkçe, İngilizce, Almanca, Rusça ve İspanyolca dil seçenekleri
• Mobil Uyumlu Duyarlı Tasarım: Akıllı telefonlar, tabletler ve bilgisayarlarda tam performanslı WebGL deneyimi`,
      en: `Battleship.pro (3D Battleship) is a modern reimagining of the classic naval tactical board game powered by WebGL 3D graphics and real-time WebSocket (Socket.io) networking.

Key Features:

• 3D Real-Time Naval Arena: Dynamic 3D battlefield with customizable camera views, atmospheric ocean shaders and explosion effects
• Online Multiplayer: Instant room creation, custom room codes, shareable invite links and automatic quick matchmaking
• AI Solo Battles: Test your tactical skills against AI admirals across 3 difficulty tiers (Cadet, Tactician, Admiral)
• Leaderboards & Stats: Real-time tracking of win/loss ratios, accuracy percentages and global player rankings
• Interactive Fleet Intel: Live damage and health tracking for Aircraft Carrier, Battleship, Cruiser, Submarine and Destroyer
• Multi-Language Support: Available in Turkish, English, German, Russian and Spanish
• Mobile-Optimized UX: Responsive web controls optimized for smartphones, tablets and desktop computers`,
    },
    image: battleshipImage,
    appStoreLink: '#',
    playStoreLink: '#',
    technologies: ['Three.js', 'Socket.io', 'WebGL', 'Node.js'],
    projectLink: '/projects/battleship',
    development: false,
    websiteLink: 'https://battleship.pro',
  },
  {
    id: 'csv-formatter',
    title: {
      tr: 'CSV Formatter & Mapper',
      en: 'CSV Formatter & Mapper',
    },
    description: {
      tr: 'CSV Formatter & Mapper, farklı kaynaklardan alınan CSV verilerini hedef şablonlara görsel sürükle-bırak yöntemiyle eşleyen, sütun birleştirme, sıralı ID ve dinamik kurallarla dönüştüren %100 tarayıcı içi akıllı veri aktarım aracıdır.',
      en: 'CSV Formatter & Mapper is a high-performance, 100% client-side web application for visual drag-and-drop CSV field mapping, rule-based transformation, sequence ID generation, and live table preview.',
    },
    fullDescription: {
      tr: `CSV Formatter & Mapper, işletmelerin, geliştiricilerin ve e-ticaret satıcılarının farklı tedarikçi veya platformlardan aldıkları karmaşık CSV verilerini hedef şemalara (OTS, ERP, Shopify, muhasebe sistemleri vb.) saniyeler içinde dönüştürmesini sağlayan profesyonel bir web uygulamasıdır. Tüm veri işleme işlemleri %100 tarayıcı üzerinde (istemci tarafında) çalışır; verileriniz hiçbir sunucuya yüklenmez.

Temel Özellikler:

• Görsel Sürükle-Bırak Eşleme: Kaynak CSV sütunlarını hedef alan yuvalarına sürükleyip bırakarak sezgisel ve hızlı eşleme yapın
• 1 Tıkla Akıllı Otomatik Eşleme (Auto-Match): Benzer başlık isimlerini algoritmik benzerlik analiziyle tek tıkla otomatik bağlayın
• Sütun Birleştirme (Concat): Birden çok kaynak alanı (örn. Ad ve Soyad) araya özel ayraçlar koyarak tek hedef sütunda birleştirin
• Sıralı ID & Sayaç Üretimi: Başlangıç numarası, artış miktarı, ön ek (örn. OTS-) ve sıfır tamamlama (örn. OTS-0054) ile otomatik kimlik numaraları oluşturun
• Sayı & Para Formatı Normalizasyonu: Avrupa ve Türkiye formatındaki ondalık/binlik ayrımı içeren sayıları (1.250,50 ➔ 1250.50) veritabanı ve ERP uyumlu hale getirin
• Metin Dönüşümleri & Temizleme: Büyük harf, küçük harf, kelime baş harfi büyütme, boşluk temizleme ve sadece rakam filtreleme
• Canlı Veri Önizleme & Arama: Dönüştürülmüş verileri kaynak verilerle yan yana eşzamanlı inceleyin, filtreleyin ve sayfalayın
• Excel UTF-8 BOM Koruması: Microsoft Excel'de Türkçe ve yabancı karakterlerin bozulmasını önleyen UTF-8 BOM desteği ile Noktalı Virgül (;), Virgül (,), Tab ve Pipe ayraçlarıyla dışa aktarın
• Şablon (Preset) Yönetimi & Yerel Hafıza: Düzenli tekrarlanan CSV aktarımlarını tek saniyelik işe dönüştürmek için eşleme kurallarınızı şablon olarak kaydedin
• 35 Dünya Dili & RTL Desteği: Türkçe, İngilizce, Almanca, Arapça ve İbranice dahil 35 dilde tam arayüz ve sağdan sola (RTL) yerleşim desteği
• %100 Gizlilik & Güvenlik: HTML5 File API ve Web Worker altyapısıyla tüm veriler cihazınızda işlenir, sunucuya hiçbir veri gönderilmez`,
      en: `CSV Formatter & Mapper is a professional, high-performance web tool designed for businesses, e-commerce managers, and developers to transform third-party vendor CSV files into any destination schema (OTS, ERP, Shopify, accounting platforms) in seconds. All processing executes 100% locally in your browser with zero server transmission.

Key Features:

• Visual Drag & Drop Mapping: Intuitive card-based column mapping between source and destination schema slots
• 1-Click Smart Auto-Match: Automated fuzzy similarity matching between source headers and target fields
• Column Concatenation: Merge multiple source fields (e.g., FirstName and LastName) with custom delimiters into a single target column
• Sequence Number & Auto-Increment IDs: Generate sequenced IDs from any starting number with configurable steps, prefixes (e.g. OTS-), and zero-padding (e.g. OTS-0054)
• Numeric & Currency Normalization: Standardize European and Turkish decimal/comma numbers (1.250,50 ➔ 1250.50) for databases and ERP systems
• Text Transformations: Uppercase, lowercase, capitalize, whitespace trimming, and numeric-only barcode filters
• Live Reactive Data Preview: Real-time side-by-side comparison of original and converted rows with pagination and search
• Excel UTF-8 BOM Protection: Export with UTF-8 BOM to prevent character corruption in Excel, supporting Semicolon (;), Comma (,), Tab, and Pipe delimiters
• Saved Presets & Local Cache: Save mapping rules to presets so recurring data imports become a 1-click routine
• 35 World Languages with RTL: Full localization in 35 languages including English, Turkish, German, Arabic, Hebrew, and Urdu with native RTL layout
• 100% Client-Side Privacy: Powered by HTML5 File API and Web Workers; customer data never leaves your browser`,
    },
    image: csvFormatterImage,
    appStoreLink: '#',
    playStoreLink: '#',
    technologies: ['React', 'Vite', 'PapaParse', 'Web Workers', 'i18next'],
    projectLink: '/projects/csv-formatter',
    development: false,
    websiteLink: 'https://csv.efestech.com',
  },
]

function resolveProject(data: ProjectData, lang: 'tr' | 'en'): Project {
  return {
    id: data.id,
    title: data.title[lang],
    description: data.description[lang],
    fullDescription: data.fullDescription?.[lang],
    image: data.image,
    appStoreLink: data.appStoreLink,
    playStoreLink: data.playStoreLink,
    technologies: data.technologies,
    projectLink: data.projectLink,
    development: data.development,
    websiteLink: data.websiteLink,
  }
}

export const useProjectsStore = defineStore('projects', () => {
  const languageStore = useLanguageStore()

  const getAllProjects = computed<Project[]>(() =>
    projectsData.map((p) => resolveProject(p, languageStore.currentLanguage)),
  )

  const getProjectById = computed(
    () =>
      (id: string): Project | undefined => {
        const data = projectsData.find((p) => p.id === id)
        if (!data) return undefined
        return resolveProject(data, languageStore.currentLanguage)
      },
  )

  return {
    getAllProjects,
    getProjectById,
  }
})
